// src/constants.js
// Extracted from App.jsx — behavior-preserving split (2026-07-20).

import { BRAND, C } from './theme';

// The live pipeline, front to back (Reece, 10/1/26: "let's simplify these
// statuses.. There's too many"). Ten stages:
//   Prospect — someone to work: a call-in, a new lead, a revived file. Due
//              call backs also show in this lane on the board (display only
//              — see the Prospect lane in Pipeline).
//   Pitched  — a scenario went out (Calc's pitch graduates a Prospect here).
//   Scheduled Counseling … Funded — the file moving through the lender.
// Order matters: stageAtOrPast() is index-based (LOCK_STAGE, the counseling
// card and the appraisal card all read it).
const STAGES=["Prospect","Pitched","Scheduled Counseling","Docs Out","Appraisal Ordered","Underwriting","Approved","Clear to Close","Closed","Funded"];
const STC={"Prospect":"#7A8AA8","Pitched":C.orange,"Scheduled Counseling":"#D97706","Docs Out":"#CA8A04","Appraisal Ordered":"#2B8ACF","Underwriting":C.purple,"Approved":"#7C5CDB","Clear to Close":"#10A570","Closed":C.green,"Funded":C.cyan};

// ── The old stage names (before 10/1/26) → the stage each one became ──
// Rows saved under an old name stay that way until
// supabase/2026-10-01-stages-simplified.sql is run (and in any browser's
// localStorage cache), so every place a stage string arrives from the
// database or a server endpoint runs it through normalizeStage() —
// loanFromDb, apiLoanToLocal, loadState — and loanToDb writes the new name
// back on the file's next save (the autosave merge, mergeLoanRow in App.jsx,
// normalizes the server's copy too, so an edit to any other field still
// moves it forward). The SQL uses this same mapping: keep the two in step.
const LEGACY_STAGE_MAP={
  "Inquiry":"Prospect",
  "Ready for Review":"Docs Out",
  "Appraisal Scheduled":"Appraisal Ordered",
  "Appraisal Done":"Appraisal Ordered",
  "Submit to Lender":"Underwriting",
  "Approval":"Approved",
  "CTC Review":"Approved",
  "CTC":"Clear to Close",
  "Scheduled":"Clear to Close",
};
// The stage as the app knows it today. A legacy name maps forward; a current
// stage typed in any case / spacing comes back spelled exactly as in STAGES;
// anything else (a fallout stage, "", null) is returned untouched, so the
// caller's own default still applies.
const normalizeStage=function(stage){
  if(typeof stage!=="string")return stage;
  if(Object.prototype.hasOwnProperty.call(LEGACY_STAGE_MAP,stage))return LEGACY_STAGE_MAP[stage];
  if(STAGES.indexOf(stage)>=0)return stage;
  var t=stage.replace(/\s+/g," ").trim().toLowerCase();
  if(!t)return stage;
  var i;
  for(i=0;i<STAGES.length;i++){if(STAGES[i].toLowerCase()===t)return STAGES[i];}
  var keys=Object.keys(LEGACY_STAGE_MAP);
  for(i=0;i<keys.length;i++){if(keys[i].toLowerCase()===t)return LEGACY_STAGE_MAP[keys[i]];}
  return stage;
};


// ── FALLOUT STAGES ────────────────────────────────────────────
// Where a worked call lands when it doesn't become a pitch. Three doors, and
// the difference between them is the whole point:
//   Qualified → priced fine, wrong moment. Stays on a follow-up clock.
//   Denied    → priced, doesn't work. Reason kept so it isn't re-run blind.
//   No App    → never got priced at all. Not a dead deal — an unknown one.
// They replace the old "just archive it" dead end: a fallout file keeps every
// number, note and timestamp that was gathered on the call.
//
// They are deliberately NOT members of STAGES. stageAtOrPast() is index-based,
// so a fallout file appearing anywhere in that array would read as "past" the
// live stages and unlock counseling/doc logic. Out of the array, indexOf()
// returns -1 and every stageAtOrPast() check is correctly false.
//
// Fallout files also carry archived:true so every pre-existing "hide archived"
// filter (Dashboard, Pipeline lanes, Doc Review counts) keeps working with no
// changes. The Fallout board in Pipeline is where they surface.
const FALLOUT_QUAL="Fallout — Qualified";
const FALLOUT_DENIED="Fallout — Denied";
// No App = the call happened but the numbers never got run. A quick question
// answered, someone fishing for information, the wrong person on the line.
// Kept as its own stage rather than lumped in with Denied because these are
// NOT dead deals — nobody ever established whether they qualify. They are the
// cheapest future target list in the business: a real human who picked up the
// phone about a reverse mortgage.
const FALLOUT_NOAPP="Fallout — No App";
const FALLOUT_STAGES=[FALLOUT_QUAL,FALLOUT_DENIED,FALLOUT_NOAPP];
STC[FALLOUT_QUAL]=C.gold;
STC[FALLOUT_DENIED]=C.red;
STC[FALLOUT_NOAPP]=C.cyan;
// Marketing tag written on the source lead at disposition, so these lists stay
// targetable from the Marketing view long after the call.
const FALLOUT_TAGS={};
FALLOUT_TAGS[FALLOUT_QUAL]="fallout-qual";
FALLOUT_TAGS[FALLOUT_DENIED]="fallout-denied";
FALLOUT_TAGS[FALLOUT_NOAPP]="fallout-noapp";

// Why the deal fell out — picked at disposition time, stored on the file so
// the Fallout board can be read at a glance ("who do I call back in 90 days").
const FALLOUT_REASONS={};
FALLOUT_REASONS[FALLOUT_QUAL]=[
  "Not ready — call back later",
  "Wants to think it over",
  "Spouse / family needs to weigh in",
  "Shopping other lenders",
  "Waiting on home value / market",
  "Moving or selling soon",
  "Numbers work — wants to pay the balance down first",
  "Couldn't reach a decision maker",
  "Other — see notes",
];
// No App reasons describe how the CALL ended, not how the deal priced —
// nothing was priced. Keep them honest enough to be worth filtering on later.
FALLOUT_REASONS[FALLOUT_NOAPP]=[
  "Quick question — answered it",
  "Just wanted information",
  "Wouldn't give enough to run numbers",
  "Not the homeowner",
  "Wrong number / not the person",
  "Call cut short — try again",
  "Already working with someone else",
  "Calling about something else entirely",
  "Asked to be removed from mailings",
  "Other — see notes",
];
FALLOUT_REASONS[FALLOUT_DENIED]=[
  "Too young (under 62)",
  "Not enough equity",
  "Mandatory obligations exceed the principal limit",
  "Property type ineligible",
  "Not the primary residence",
  "Fails the benefit test",
  "Property charge / credit history",
  "Already has a reverse — no benefit",
  "Not interested",
  "Wrong number / not the person",
  "Other — see notes",
];

// Rolling caps on the per-file history we keep inside follow_ups._wf. The
// column is jsonb on a row we upsert constantly — unbounded growth there is
// how a save turns into a 2MB payload.
const QUOTE_LOG_MAX=12;
const CONTACT_LOG_MAX=40;
// Swap-rate marks kept in settings.rate_sheets._swapHistory. Small rows, and a
// year of daily marks is only ~250 — this cap is a backstop, not a constraint.
const SWAP_HISTORY_MAX=400;

// localStorage ring buffer of inbound RingCentral calls. The toast used to be
// the ONLY record of a call and it auto-dismissed after 30 seconds — a missed
// call while away from the desk left no trace at all. Per-device on purpose:
// this is "what rang on this machine", not shared CRM data.
const CALL_LOG_KEY="loanflow_calls_v1";
const CALL_LOG_MAX=60;

// Direct mail referral campaign tags — cross-reference marketing pieces
const DM_CAMPAIGNS=["DM-2024-Q4-Refi","DM-2025-Q1-Purchase","DM-2025-Q1-Equity","DM-2025-Q2-Rate","DM-2025-Q3-General","Other / Unknown"];
const ENVELOPE_TYPES=["#9 Standard","#10 Standard","#10 Window","6x9 Booklet","A7 Invitation","9x12 Flat","Custom"];
const RETURN_ADDR_FORMATS=["Handwritten (Image)","Printed – Summit Branded","Printed – Plain","None / Blank"];
const STAMP_STYLES=["Live Stamp","Metered","Presorted Standard","First Class Indicia","Forever Stamp","None"];
const MAIL_METHODS=["Handwritten","Printed","Mixed (Handwritten Envelope / Printed Letter)"];
const TOUCH_INTERVAL_DAYS = 21;

// Lender submission checklists
const LENDER_CHECKLISTS={
  "Finance of America":[
    {id:"app",label:"HECM Application (1009 + Supplement)",required:true},
    {id:"cert_auth",label:"Certification & Authorization",required:true},
    {id:"counseling",label:"HUD Counseling Certificate (within 180 days)",required:true},
    {id:"appraisal",label:"FHA Appraisal (URAR + addenda)",required:true},
    {id:"title",label:"Title Commitment / Prelim",required:true},
    {id:"ins_dec",label:"Homeowner's Insurance Dec Page",required:true},
    {id:"id_copy",label:"Photo ID — All Borrowers",required:true},
    {id:"fha_case",label:"FHA Case Number Assignment",required:true},
    {id:"hoa_cert",label:"HOA Certification (condo only)",required:false},
    {id:"fha_condo",label:"FHA Condo Approval (condo only)",required:false},
    {id:"payoff",label:"Payoff Statement (if existing mortgage)",required:false},
    {id:"income",label:"Social Security Award Letter / Income Docs (if LESA)",required:false},
    {id:"lesa_docs",label:"12 Month Bank Statements (LESA only)",required:false},
    {id:"trust",label:"Trust Documents (if titled in trust)",required:false},
    {id:"poa",label:"Power of Attorney (if applicable)",required:false},
  ],
  "Mutual of Omaha":[
    {id:"app",label:"HECM Application (1009 + Supplement)",required:true},
    {id:"cert_auth",label:"Certification & Authorization",required:true},
    {id:"counseling",label:"HUD Counseling Certificate (within 180 days)",required:true},
    {id:"appraisal",label:"FHA Appraisal (URAR + addenda)",required:true},
    {id:"title",label:"Title Commitment / Prelim",required:true},
    {id:"ins_dec",label:"Homeowner's Insurance Dec Page (12 mo paid receipt)",required:true},
    {id:"id_copy",label:"Photo ID — All Borrowers",required:true},
    {id:"fha_case",label:"FHA Case Number Assignment",required:true},
    {id:"tax_cert",label:"Property Tax Certification (current year)",required:true},
    {id:"hoa_cert",label:"HOA Certification (condo only)",required:false},
    {id:"fha_condo",label:"FHA Condo Approval (condo only)",required:false},
    {id:"payoff",label:"Payoff Statement (if existing mortgage)",required:false},
    {id:"ss_award",label:"Social Security Award Letter",required:false},
    {id:"bank_stmts",label:"12 Month Bank Statements (LESA only)",required:false},
    {id:"trust",label:"Trust Documents (if titled in trust)",required:false},
    {id:"poa",label:"Power of Attorney (if applicable)",required:false},
    {id:"flood",label:"Flood Insurance Dec Page (flood zone)",required:false},
  ],
  "Generic / Other":[
    {id:"app",label:"HECM Application (1009 + Supplement)",required:true},
    {id:"cert_auth",label:"Certification & Authorization",required:true},
    {id:"counseling",label:"HUD Counseling Certificate (within 180 days)",required:true},
    {id:"appraisal",label:"FHA Appraisal",required:true},
    {id:"title",label:"Title Commitment",required:true},
    {id:"ins_dec",label:"Homeowner's Insurance Dec Page",required:true},
    {id:"id_copy",label:"Photo ID — All Borrowers",required:true},
    {id:"fha_case",label:"FHA Case Number Assignment",required:true},
    {id:"payoff",label:"Payoff Statement (if applicable)",required:false},
    {id:"hoa_cert",label:"HOA / Condo Docs (if applicable)",required:false},
    {id:"income",label:"Income Docs / LESA Docs (if applicable)",required:false},
    {id:"trust",label:"Trust Documents (if applicable)",required:false},
  ],
};

const LOANS_INIT=[
  {id:"LN-001",b:"Margaret Thompson",ph:"(602) 555-1234",em:"m.thompson@email.com",prop:"4521 Maple Ridge Dr, Phoenix AZ",type:"HECM",stage:"Docs Out",condo:false,fha:null,age:72,hv:385000,mb:95000,src:"Referral",ref:"Tom Bradley",comm:4200,estClose:"05/15/2026",docs:{hoa:"pending",payoff:"received",ins:"pending",app:"flagged"},followUps:{hoa:3,ins:2},uploads:[],deal:{},statementData:null,tasks:["Send HOA request","Follow up insurance","Review application flags"]},
  {id:"LN-002",b:"Robert & Linda Chen",ph:"(480) 555-5678",em:"chen@email.com",prop:"Unit 812, Skyline Towers, Scottsdale AZ",type:"FHA",stage:"Docs Out",condo:true,fha:"Approved",age:68,hv:520000,mb:210000,src:"Mailer",dmCampaign:"DM-2025-Q1-Equity",ref:null,comm:5800,estClose:"05/22/2026",docs:{hoa:"sent",payoff:"pending",ins:"received",app:"clear"},followUps:{hoa:1},uploads:[],deal:{},statementData:null,tasks:["Follow up payoff request","Order title"]},
  {id:"LN-003",b:"David Williams",ph:"(602) 555-9012",em:"d.williams@email.com",prop:"1100 Desert Willow Ct, Tempe AZ",type:"HECM",stage:"Appraisal Ordered",condo:false,fha:null,age:74,hv:310000,mb:0,src:"Mailer",dmCampaign:"DM-2025-Q1-Purchase",ref:null,comm:3500,estClose:"06/10/2026",docs:{hoa:null,payoff:null,ins:null,app:null},followUps:{},uploads:[],deal:{},statementData:null,appraisalOrdered:"04/01/2026",appraisalScheduled:"",appraisalDone:"",tasks:["Send application package","Run calculator with borrower"]},
  {id:"LN-004",b:"Patricia Gonzalez",ph:"(480) 555-3456",em:"p.gonzalez@email.com",prop:"Unit 3B, Palm Vista Condos, Mesa AZ",type:"FHA",stage:"Underwriting",condo:true,fha:"Expired",age:66,hv:275000,mb:135000,src:"Referral",ref:"Maria Santos",comm:3100,estClose:"05/01/2026",docs:{hoa:"received",payoff:"received",ins:"received",app:"flagged"},followUps:{},uploads:[],deal:{},statementData:null,tasks:["Resolve app flags — counseling form","Support UW conditions"]},
  {id:"LN-005",b:"William & Nancy Park",ph:"(602) 555-7890",em:"parks@email.com",prop:"2299 Cactus Bloom Ln, Gilbert AZ",type:"HECM",stage:"Clear to Close",condo:false,fha:null,age:70,hv:450000,mb:180000,src:"Mailer",dmCampaign:"DM-2025-Q2-Rate",ref:null,comm:4900,estClose:"04/12/2026",docs:{hoa:"received",payoff:"received",ins:"received",app:"clear"},followUps:{},uploads:[],deal:{},statementData:null,tasks:["Schedule closing","Confirm borrower ready"]},
  {id:"LN-006",b:"Helen Martinez",ph:"(480) 555-2345",em:"h.martinez@email.com",prop:"Unit 15A, Sunrise Lofts, Phoenix AZ",type:"HECM",stage:"Docs Out",condo:true,fha:"N/A",age:78,hv:340000,mb:45000,src:"Seminar",ref:"Lisa Tran",comm:3800,estClose:"06/01/2026",docs:{hoa:"pending",payoff:"sent",ins:"pending",app:"pending"},followUps:{hoa:4,payoff:1,ins:3},uploads:[],deal:{},statementData:null,tasks:["URGENT: HOA 4 days overdue","Follow up insurance","Follow up payoff","Review app when ready"]},
  {id:"LN-007",b:"Frank & Rose Dubois",ph:"(602) 555-6789",em:"dubois@email.com",prop:"887 Sunstone Way, Chandler AZ",type:"HECM",stage:"Pitched",condo:false,fha:null,age:69,hv:295000,mb:75000,src:"Mailer",dmCampaign:"DM-2025-Q3-General",ref:null,comm:3200,estClose:"07/01/2026",docs:{hoa:null,payoff:null,ins:null,app:null},followUps:{},uploads:[],deal:{},statementData:null,tasks:["Initial outreach call","Qualify lead"]},
];

// ── Stage helpers — identity info locks once a deal reaches counseling ──
const LOCK_STAGE="Scheduled Counseling";

// ── "Add a deal" (Pipeline) — a deal that was priced OUTSIDE the calculator ──
// Reece closed a HomeSafe Second in the lender's software (10/1/26) with no
// way to enter it. Lenders as the calculator names them (FOA is "Finance of
// America" there and in LENDER_CHECKLISTS). Programs are the calculator's
// product families; `rate` says whether the figure he types is a note rate or
// a margin, `lender` is the default pick, `second` marks a second lien (the
// balance he types is the first lien that stays in place, not a payoff).
const MANUAL_DEAL_LENDERS=[
  {value:"Finance of America",label:"FOA (Finance of America)"},
  {value:"SmartFi",label:"SmartFi"},
  {value:"Longbridge",label:"Longbridge"},
  {value:"Mutual of Omaha",label:"Mutual of Omaha"},
  {value:"Other",label:"Other"},
];
const MANUAL_DEAL_PROGRAMS=[
  {name:"HECM ARM",lender:"",rate:"margin"},
  {name:"HECM Fixed",lender:"",rate:"rate"},
  {name:"HomeSafe Standard",lender:"Finance of America",rate:"rate"},
  {name:"HomeSafe Intro",lender:"Finance of America",rate:"rate"},
  {name:"HomeSafe Select Intro",lender:"Finance of America",rate:"margin"},
  {name:"HomeSafe Second",lender:"Finance of America",rate:"rate",second:true},
  {name:"SecureEquity Fixed",lender:"Mutual of Omaha",rate:"rate"},
  {name:"SecureEquity Fixed Plus",lender:"Mutual of Omaha",rate:"rate"},
  {name:"SecureEquity ARM",lender:"Mutual of Omaha",rate:"margin"},
  {name:"Choice Fixed",lender:"SmartFi",rate:"rate"},
  {name:"Choice Flex",lender:"SmartFi",rate:"margin"},
  {name:"Platinum Fixed",lender:"Longbridge",rate:"rate"},
  {name:"Platinum LOC",lender:"Longbridge",rate:"margin"},
  {name:"Platinum Preserve",lender:"Longbridge",rate:"rate"},
  {name:"Other",lender:"",rate:"rate"},
];
// What every hand-entered deal says about itself, wherever it is shown.
const MANUAL_DEAL_LABEL="Entered by hand";
// A HomeSafe Second file's loan type (loans.loan_type) — the deal type
// "hs2" on the New Call card / Calc's HomeSafe Second mode. A second lien
// behind a forward first that stays in place, not a reverse refi.
const HS2_LOAN_TYPE="HomeSafe Second";
// Per-file click-to-call history, kept in follow_ups._wf (see wfPack).
const CALL_CLICKS_MAX=100;

const LS_KEY="loanflow_v1";
// Above this row count we skip mktgLeads from localStorage proactively.
// Supabase is the source of truth for marketing leads — localStorage was
// only buying us an offline-tab fallback, and at 500+ rows the cost of
// stringifying every save outweighs that benefit. Lowered from 1000 →
// 500 in May 2026 after a quota crash at ~2000 leads caused a blank
// screen on production.
const LS_LEADS_SKIP_THRESHOLD=500;

// ── Borrower document-request template ──
// Drives the "Send portal access" email + the portal's required-docs list.
// Two groups: Application Docs (e-signed via the Quantum link the admin
// pastes) and Supporting Docs. `on` = checked by default. The income item
// links to SSA.gov; the application item carries the Quantum signing link.
var SSA_ACCOUNT_URL="https://www.ssa.gov/myaccount/";
// PostgREST .or() filter matching only the leads the urgent banner and the
// Dashboard statement panel care about: portal-intake rows (inbound-callback /
// inbound-statement), opt-out requests, and any row marked as a statement
// submission by list_source (belt-and-suspenders: some rows carry the
// list_source label without the tag, and the panel must never miss one).
// At 45K+ leads this returns dozens of rows instead of the whole table.
const ALERT_LEADS_FILTER='tags.cs.["inbound-callback"],tags.cs.["inbound-statement"],tags.cs.["inbound-inquiry"],tags.cs.["optout-request"],list_source.eq."Statement Submission"';
// ── FHA HECM national lending limit (max claim amount) by year ──
// Used by the Outbound dialer's "Lending limit" filter: a HECM whose home
// value was above the limit the year it closed was capped then, so today's
// higher limit (settings.hecmLimit) can mean more money. Pre-2017 = $625,500.
const HECM_LIMIT_BY_YEAR={2017:636150,2018:679650,2019:726525,2020:765600,2021:822375,2022:970800,2023:1089300,2024:1149825,2025:1209750,2026:1249125};
const hecmLimitForYear=function(y,current){if(!y)return null;if(y<2017)return 625500;return HECM_LIMIT_BY_YEAR[y]||current||null;};

// ── Working set: the leads loaded at login ──
// Anything mailed, worked, flagged or linked (~1/3 of the table). The
// never-mailed remainder (~74K) stays in the database: Marketing loads it on
// request, and New Call's Find searches it server-side. A lead enters this set
// automatically once it is mailed, dispositioned, noted in New Call (which
// writes the {type:"profile"} history entry) or given a follow-up.
const WORKING_LEADS_FILTER='status.neq.New,responded.is.true,do_not_contact.is.true,follow_up_date.not.is.null,converted_loan_id.not.is.null,mailings.neq.[],history.cs.[{"type":"profile"}],'+ALERT_LEADS_FILTER;


// ═══════ URGENT ALERT BANNER ═══════
// Persistent red, pulsing banner shown above the admin layout whenever there
// are unacknowledged borrower-initiated events: callback requests, document
// uploads, statement submissions, or new portal-intake leads. Acknowledgement
// is tracked per-device in localStorage so dismissing on phone vs laptop is
// independent. Initial ack is set to "now" on first load so historical events
// don't suddenly flag.
const URGENT_ACK_KEY="loanflow_urgent_ack";
// ═══════ SETTINGS ═══════
// "Delete All Data" (Settings → Danger Zone) wipes every table. Never in a
// production build: CRA inlines NODE_ENV, so the button and the wipe code drop
// out of the shipped bundle entirely. In local dev it also needs a localhost
// page, so a dev build pointed at prod data from another host can't show it.
// Settings additionally hides it whenever Supabase is connected: local dev
// uses the production project, so the wipe only ever clears localStorage.
const ALLOW_DATA_WIPE=process.env.NODE_ENV!=="production"&&typeof window!=="undefined"&&!!window.location&&/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname||"");

// ── Licensed states ──
// The two-letter codes in BRAND.licensedStates (theme.js is the one source —
// the footer prints that string). Compliance text only: nothing in the app
// gates on it (Reece, 10/1: "I want to always be able to run deals no matter
// what") — the calculator prices every state and Outbound dials every state.
const LICENSED_STATES=String(BRAND.licensedStates||"").split(/[\s,]+/).map(function(s){return s.trim().toUpperCase();}).filter(function(s){return /^[A-Z]{2}$/.test(s);});
const isLicensedState=function(st){var s=String(st==null?"":st).trim().toUpperCase();return s.length===2&&LICENSED_STATES.indexOf(s)>=0;};

export { LEGACY_STAGE_MAP, normalizeStage, MANUAL_DEAL_LENDERS, MANUAL_DEAL_PROGRAMS, MANUAL_DEAL_LABEL, HS2_LOAN_TYPE, LICENSED_STATES, isLicensedState, hecmLimitForYear, HECM_LIMIT_BY_YEAR, WORKING_LEADS_FILTER, CALL_CLICKS_MAX, STAGES, STC, FALLOUT_QUAL, FALLOUT_DENIED, FALLOUT_NOAPP, FALLOUT_STAGES, FALLOUT_REASONS, FALLOUT_TAGS, QUOTE_LOG_MAX, CONTACT_LOG_MAX, SWAP_HISTORY_MAX, CALL_LOG_KEY, CALL_LOG_MAX, DM_CAMPAIGNS, ENVELOPE_TYPES, RETURN_ADDR_FORMATS, STAMP_STYLES, MAIL_METHODS, TOUCH_INTERVAL_DAYS, LENDER_CHECKLISTS, LOANS_INIT, LOCK_STAGE, LS_KEY, LS_LEADS_SKIP_THRESHOLD, SSA_ACCOUNT_URL, ALERT_LEADS_FILTER, URGENT_ACK_KEY, ALLOW_DATA_WIPE };
